import { faBookmark, faClouds, faGear, faLaptop, faQuestion } from '@fortawesome/pro-regular-svg-icons';
import { faBookmark as faSolidBookMark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonDropdown, ButtonGroup, NotificationDispatch, StyledFlex, VotingButtons, useViewport } from '@tryhackme/thm-ui-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { clearTaskOrderOverride, setTaskOrderOverride, setVmStartType } from 'src/app/reducers/room/room.reducer';
import { setVmPanelOpen } from 'src/app/reducers/split-screen.reducer';
import { store } from 'src/app/store';
import { BREAKPOINTS } from 'src/common/constants/breakpoints';
import { ROOM_CODE_ATTACK_BOX_FOR_FREE_USERS, ROOM_CODE_ATTACK_BOX_PREMIUM_OR_KALI } from 'src/common/constants/vms';
import { AWSAccountState, HttpStatus, UserRoles } from 'src/common/enums';
import { RoomUserEventType } from 'src/common/enums/rooms';
import { TaskType } from 'src/common/enums/task';
import { isInternalContentDev } from 'src/common/helpers';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { useMediaQuery } from 'src/common/hooks/use-media-query';
import { useStartVm } from 'src/common/hooks/use-start-vm';
import { NetworkInstanceStatus } from 'src/common/interfaces/networks/instances';
import type { VmTypes } from 'src/common/interfaces/vms';
import { useGetCompanyQuery, useGetUserQuery, useTrackEventMutation } from 'src/common/slices';
import { addSocketListener, removeSocketListener } from 'src/common/sockets';
import { useRequestAzureLabMutation } from 'src/features/admin/cloud-training/azure-tenant-onboarding.slice';
import { useGetAwsCredentialsQuery, useGetAwsEnvDetailsQuery } from 'src/features/api/rooms/aws/aws.slice';
import { useGetBadgesQuery } from 'src/features/badges/badges.slice';
import { useIsCurrentTeamOwnerQuery } from 'src/features/manage-account/teams/team.slice';
import { ModalLayout } from 'src/features/manage-room/components';
import { useCloneRoomMutation } from 'src/features/manage-room/manage-room.slice';
import { useAddSavedRoomMutation, useRemoveSavedRoomMutation } from 'src/features/my-rooms/my-rooms.slice';
import { DesktopOnlyAccessPopup } from 'src/features/room/components/desktop-only-access-popup';
import { LeaveRoomModal } from 'src/features/room/components/leave-room-modal';
import { ResetProgressModal } from 'src/features/room/components/reset-progress-modal';
import { AWSCloudDetailsModal } from 'src/features/room/components/room-banner/components/aws-cloud-details/aws-cloud-details-modal';
import { AWSCloudDetailsPopup } from 'src/features/room/components/room-banner/components/aws-cloud-details/aws-cloud-details-popup';
import { ShowWriteups } from 'src/features/room/components/writeup-modals';
import type { Writeup } from 'src/features/room/components/writeup-modals/writeup-modal.types';
import { getHelpOptions } from 'src/features/room/helpers';
import { useRoomCode } from 'src/features/room/hooks/use-room-replacement-experiment';
import { ROOM_USER_EVENT } from 'src/features/room/room.constants';
import { useGetRoomDetailQuery, useGetRoomVotesQuery, useSetRoomVotesMutation } from 'src/features/room/room.slice';
import type { RoomCloudEnvEvent } from 'src/features/room/room.types';
import { AwardsBadgePopUp } from '../../../awards-badge-popup';
import { useRoomTaskBreakdownContext } from '../../../task-breakdown/components/room-task-breakdown-context';
import { BROWSER_MACHINE_OPTIONS, HELPOPTIONSDROPDOWN, OPTIONS, OPTIONSDROPDOWN } from '../../room-banner.constants';
import { type AWSCredentials, EnvironmentStatusOptions } from '../aws-cloud-details/aws-cloud-details.types';
import { Certificate } from '../certificates';
import { CompletionShareRoomModal } from '../completion-modal-share-roomcompletion-share-room-modal';
import { StyledLoading } from '../loading/loading.styles';
import { ShareAoCModal } from '../share-aoc-modal';
import { StyledButton as UIStyledButton } from '@tryhackme/thm-ui-components';
import { AZURE_ENTRA_ID_ROOM_CODES } from './room-banner-actions.constants';
import { StyledButton, StyledChildButton, StyledWrapperAttackBoxButton } from './room-banner-actions.styles';
import type { RoomBannerActionsProps } from './room-banner-actions.type';
export const RoomBannerActions = ({
  roomDetails,
  isLoading,
  isRetiredRoom,
  isPrivateOrRetiredRoom,
  setRoomProgress,
  roomProgress,
  setShowVideo,
  splitScreenTabs,
  vmStartType
}: RoomBannerActionsProps) => {
  const {
    isTaskBreakdownSupported,
    resetToFirstTask
  } = useRoomTaskBreakdownContext();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const roomCode = useRoomCode();
  if (!roomCode) throw new Error('No roomCode in url params');
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [openResetProgressModal, setOpenResetProgressModal] = useState<boolean>(false);
  const [openLeaveRoomModal, setOpenLeaveRoomModal] = useState<boolean>(false);
  const [openCloneRoomModal, setOpenCloneRoomModal] = useState<boolean>(false);
  const [leaveAsTeam, setLeaveAsTeam] = useState<boolean>(false);
  const [openWriteupsModal, setOpenWriteupsModal] = useState<boolean>(false);
  const [isOpenDesktopOnlyAccessPopUp, setIsOpenDesktopOnlyAccessPopUp] = useState<boolean>(false);
  const [isOpenAWSCloudDetailsPopup, setIsOpenAWSCloudDetailsPopup] = useState<boolean>(false);
  const [isOpenAWSCloudDetailsModal, setIsOpenAWSCloudDetailsModal] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isShareAoCModalOpen, setIsShareAoCModalOpen] = useState(false);
  const [awsEnvironmentStatus, setAwsEnvironmentStatus] = useState<EnvironmentStatusOptions>(EnvironmentStatusOptions.STARTING);
  const [awsEnvironmentRoomsList, setAwsEnvironmentRoomsList] = useState<string[]>([]);
  const [awsCredential, setAwsCredential] = useState<AWSCredentials>();
  const isAttackBoxRunning = splitScreenTabs.some(tab => tab.type === TaskType.VM && tab.vm && [ROOM_CODE_ATTACK_BOX_PREMIUM_OR_KALI, ROOM_CODE_ATTACK_BOX_FOR_FREE_USERS].includes(tab.vm.roomCode));
  const {
    refetch: refetchDetails
  } = useGetRoomDetailQuery(roomCode);
  const {
    data: votesData,
    refetch
  } = useGetRoomVotesQuery(roomCode);
  const {
    data: badgesData
  } = useGetBadgesQuery();
  const {
    data: userData,
    isSuccess: isUserAuth
  } = useGetUserQuery();
  const companyId = userData?.data?.user.subscriptions[0]?.companyId;
  const {
    data: companyData
  } = useGetCompanyQuery(companyId ?? '', {
    skip: !companyId
  });
  const {
    startVmHandler,
    isVmStartLoading
  } = useStartVm(roomCode);
  const [setRoomVotes] = useSetRoomVotesMutation();
  const [removeSavedRoom] = useRemoveSavedRoomMutation();
  const [addSavedRoom] = useAddSavedRoomMutation();
  const [trackEvent] = useTrackEventMutation();
  const [cloneRoom] = useCloneRoomMutation();
  const [searchParams] = useSearchParams();
  const [requestAzureLab] = useRequestAzureLabMutation();
  const awardedBadge = badgesData?.data?.badges?.find(badge => badge.toEarn?.includes(roomDetails?.code || ''));
  const savedRoom = roomDetails?.saved;
  const writeupsArray: Writeup[] = roomDetails?.writeups ?? [];
  const showButton = !isPrivateOrRetiredRoom && isUserAuth;
  const hasUserJoinedRoom = roomDetails?.joined;
  const remAttackBoxBtn = roomDetails?.options?.remAttackBoxBtn;
  const showMachineButton = isUserAuth && hasUserJoinedRoom && !remAttackBoxBtn && !isAttackBoxRunning;
  const isAwsCloudRoom = roomDetails?.cloud?.enabled ?? false;
  const userCanCloneRoom = roomDetails?.cloneable && userData?.data?.user?.roles?.includes(UserRoles.ROOM_MAKER);
  const isResetProgressEnabled = roomDetails?.options?.isResetProgressEnabled;
  const {
    data: isTeamOwnerData
  } = useIsCurrentTeamOwnerQuery(undefined, {
    skip: !roomDetails?.teamSettings?.enabled || !isUserAuth || !roomDetails?.joined
  });
  const isTeamOwner = isTeamOwnerData?.data;
  const isTeamsRoom = !!roomDetails?.teamSettings?.enabled && roomDetails.joined;
  const areChangesInTeamLocked = roomDetails?.teamSettings?.lockChanges;
  const isAzureCloudRoom = roomDetails?.cloud?.azure?.isEnabled ?? false;
  const isAzureEnTraIdRoom = AZURE_ENTRA_ID_ROOM_CODES.includes(roomDetails?.code ?? '');
  const showRequestTenantEnvButton = isAzureCloudRoom && isAzureEnTraIdRoom;
  const isAzureTenantLabsReady = companyData?.additionalContent?.azureTenantLabs?.labsReady;
  const isAzureTenantLabsRequested = companyData?.additionalContent?.azureTenantLabs?.labsRequested;
  const disableRequestTenantEnvButton = isAzureTenantLabsReady || isAzureTenantLabsRequested;
  const loadingRequestTenantEnvButton = isAzureTenantLabsRequested && !isAzureTenantLabsReady;
  const {
    vmPanelOpen
  } = useAppSelector(state => state.splitScreen);
  const {
    tutorPanelOpen
  } = useAppSelector(state => state.splitScreen);
  const networkDetails = useAppSelector(state => state.room.networkDetails);
  const isSplitScreenOpen = tutorPanelOpen || vmPanelOpen;
  const isTablet = useMediaQuery(`(min-width: ${BREAKPOINTS.TABLET}px)`);

  // Force user to start a network v2 first so VPN server can come up for attack box to connect
  const isNetworkV2NotReady = Boolean(roomDetails?.networkId && networkDetails?.status !== NetworkInstanceStatus.RUNNING);

  // Check if user is content dev for the unlock all tasks option
  const isContentDev = isInternalContentDev(userData?.data?.user);
  const {
    taskOrderOverride
  } = useAppSelector(state => state.room);
  let FILTERED_OPTIONS = OPTIONS.filter(option => {
    switch (option.value) {
      case OPTIONSDROPDOWN.RESET_PROGRESS:
        {
          return isResetProgressEnabled;
        }
      case OPTIONSDROPDOWN.CLONE_ROOM:
        {
          return userCanCloneRoom;
        }
      case OPTIONSDROPDOWN.LEAVE_AS_TEAM:
        {
          return isTeamsRoom && !!isTeamOwner;
        }
      case OPTIONSDROPDOWN.UNLOCK_ALL_TASKS:
        {
          // Show for content dev users only when room has task ordering enabled
          return isContentDev && roomDetails?.joined === true && roomDetails?.forceCompleteInOrder === true;
        }
      default:
        {
          return true;
        }
    }
  });
  if (isTeamsRoom) {
    FILTERED_OPTIONS = FILTERED_OPTIONS.map(option => {
      if (option.value === OPTIONSDROPDOWN.LEAVE) {
        return {
          ...option,
          title: 'Leave as a member'
        };
      }
      return option;
    });
  }

  // Update the unlock all tasks option label based on current state
  if (isContentDev) {
    FILTERED_OPTIONS = FILTERED_OPTIONS.map(option => {
      if (option.value === OPTIONSDROPDOWN.UNLOCK_ALL_TASKS) {
        return {
          ...option,
          title: taskOrderOverride.disableTaskOrdering ? 'Restore Task Ordering' : 'Unlock All Tasks',
          value: taskOrderOverride.disableTaskOrdering ? 'Restore Task Ordering' : 'Unlock All Tasks'
        };
      }
      return option;
    });
  }

  // viewport
  const {
    width
  } = useViewport();
  const executeVmClickAction = async () => {
    if (width < BREAKPOINTS.VM) {
      setIsOpenDesktopOnlyAccessPopUp(true);
    } else {
      await startVmHandler(vmStartType);
    }
  };
  const handleStartMachine = () => {
    // eslint-disable-next-line no-void
    void executeVmClickAction();
  };
  const handlerVote = async (newVote: boolean) => {
    if (roomDetails?.code) {
      await setRoomVotes({
        roomCode: roomDetails?.code,
        vote: newVote
      });
      await refetch();
    }
  };
  const handleSavedRoom = useCallback(async () => {
    try {
      if (savedRoom) {
        await removeSavedRoom(roomCode);
        await refetchDetails();
        NotificationDispatch('success', <>
            Success! Removed from <Link to="/rooms">My Rooms</Link>
          </>);
      } else {
        await addSavedRoom(roomCode);
        await refetchDetails();
        NotificationDispatch('success', <>
            Success! Saved to <Link to="/rooms">My Rooms</Link>
          </>);
      }
    } catch (error: unknown) {
      if (isQueryErrorWithMessage(error)) {
        if (error.status === HttpStatus.BAD_REQUEST) {
          NotificationDispatch('error', error.data.message);
        } else {
          NotificationDispatch('error', 'Something went wrong. Please try again.');
        }
      }
    }
  }, [savedRoom, removeSavedRoom, roomCode, refetchDetails, addSavedRoom]);
  const {
    data: cloudAwsCredentials,
    isSuccess: isCloudAwsUser
  } = useGetAwsCredentialsQuery(undefined, {
    skip: !roomDetails?.cloud?.enabled
  });
  const {
    data: cloudAwsEnvDetails,
    refetch: refetchEnvDetails
  } = useGetAwsEnvDetailsQuery(roomDetails?._id ?? '', {
    skip: !roomDetails?.cloud?.enabled
  });
  useEffect(() => {
    if (!roomDetails?.cloud) return;
    if (!cloudAwsEnvDetails?.data?.state) return;
    setAwsEnvironmentRoomsList(cloudAwsEnvDetails.data.otherRooms.length > 0 ? cloudAwsEnvDetails.data.otherRooms.map(otherRoom => otherRoom.title) : []);
    switch (cloudAwsEnvDetails.data?.state) {
      case AWSAccountState.IN_USE:
        {
          if (cloudAwsEnvDetails.data.otherRooms.length > 0 && cloudAwsEnvDetails.data.changeEnvironment) {
            setAwsEnvironmentStatus(EnvironmentStatusOptions.ANOTHER_ENVIRONMENT_ACTIVE);
          } else if (cloudAwsEnvDetails.data.otherRooms.length > 0) {
            setAwsEnvironmentStatus(EnvironmentStatusOptions.ACTIVE);
          } else {
            setAwsEnvironmentStatus(EnvironmentStatusOptions.ACTIVE_BUT_EMPTY);
          }
          break;
        }
      case AWSAccountState.ENV_GEN:
        {
          if (cloudAwsEnvDetails.data.otherRooms.length > 0) {
            setAwsEnvironmentStatus(EnvironmentStatusOptions.GENERATING);
          } else {
            setAwsEnvironmentStatus(EnvironmentStatusOptions.GENERATING_FIRST_ENVIRONMENT);
          }
          break;
        }
      case AWSAccountState.RESETTING:
        {
          setAwsEnvironmentStatus(EnvironmentStatusOptions.RESETTING);
          break;
        }
      default:
        {
          break;
        }
    }
  }, [cloudAwsEnvDetails, roomDetails?.cloud]);
  useEffect(() => {
    if (!roomDetails?.cloud) return;
    const handleCloudEnvEvent = (data: unknown) => {
      const eventData = data as RoomCloudEnvEvent;
      if (eventData.type === RoomUserEventType.ENVIRONMENT_GENERATION) {
        setIsOpenAWSCloudDetailsPopup(true);
      }
    };
    if (roomDetails?.joined) {
      addSocketListener(ROOM_USER_EVENT, handleCloudEnvEvent);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      removeSocketListener(ROOM_USER_EVENT);
    };
  }, [roomDetails]);
  useEffect(() => {
    if (isCloudAwsUser) {
      const credentials = {
        urlLink: cloudAwsCredentials?.data?.accountId ? `https://${cloudAwsCredentials.data.accountId}.signin.aws.amazon.com/console/` : '',
        username: cloudAwsCredentials?.data?.accountId || '',
        accessKeyId: cloudAwsCredentials?.data?.credentials.accessKeyId || '',
        secretAccessKey: cloudAwsCredentials?.data?.credentials.secretKey || '',
        defaultConsolePassword: cloudAwsCredentials?.data?.credentials.defaultPassword || ''
      };
      setAwsCredential(credentials);
    }
  }, [cloudAwsCredentials?.data?.accountId, cloudAwsCredentials?.data?.credentials.accessKeyId, cloudAwsCredentials?.data?.credentials.defaultPassword, cloudAwsCredentials?.data?.credentials.secretKey, isCloudAwsUser]);
  useEffect(() => {
    if (searchParams.get('reveal') === 'share') {
      setIsShareModalOpen(true);
      setIsShareAoCModalOpen(true);
    }
  }, [searchParams]);

  // Clear task order override when room changes
  useEffect(() => {
    dispatch(clearTaskOrderOverride());
  }, [dispatch]);
  const handleCloudDetails = useCallback(async () => {
    if (!roomDetails?.cloud || !roomDetails?._id) return;
    setIsOpenAWSCloudDetailsModal(true);
    await refetchEnvDetails();
  }, [refetchEnvDetails, roomDetails]);
  const selectOption = (value: string) => {
    setSelectedOption(value);
    switch (value) {
      case OPTIONSDROPDOWN.RESET_PROGRESS:
        {
          setOpenLeaveRoomModal(false);
          setOpenResetProgressModal(true);
          break;
        }
      case OPTIONSDROPDOWN.LEAVE_AS_TEAM:
        {
          setLeaveAsTeam(true);
          setOpenResetProgressModal(false);
          setOpenLeaveRoomModal(true);
          break;
        }
      case OPTIONSDROPDOWN.LEAVE:
        {
          setOpenResetProgressModal(false);
          setOpenLeaveRoomModal(true);
          break;
        }
      case OPTIONSDROPDOWN.CLONE_ROOM:
        {
          setOpenCloneRoomModal(true);
          break;
        }
      case OPTIONSDROPDOWN.UNLOCK_ALL_TASKS:
      case 'Restore Task Ordering':
        {
          const newOverrideState = {
            isOverrideEnabled: true,
            disableTaskOrdering: !taskOrderOverride.disableTaskOrdering
          };
          dispatch(setTaskOrderOverride(newOverrideState));
          NotificationDispatch('success', newOverrideState.disableTaskOrdering ? 'All tasks unlocked (testing mode)' : 'Task ordering restored');
          break;
        }
      default:
        {
          break;
        }
    }
    setSelectedOption('');
  };
  const handleRequestTenantEnv = async () => {
    if (!companyId) return;
    await requestAzureLab({
      companyId
    }).unwrap().then(() => {
      NotificationDispatch('success', 'Tenant requested successfully. Please allow up to 5 working days for provisioning.');
    }).catch(error => {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error?.data?.message : 'Something went wrong.');
    });
  };
  const handleCloneRoom = async () => {
    if (!roomDetails?._id) return;
    await cloneRoom(roomDetails._id).unwrap().then(res => {
      const clonedCode = res.data?.clonedCode;
      setOpenCloneRoomModal(false);
      NotificationDispatch('success', 'Room successfully cloned');
      // eslint-disable-next-line no-void
      void trackEvent({
        event: 'clone-room',
        properties: {
          room: roomCode
        }
      });
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      navigate(`/room/manage/${clonedCode}`);
    }).catch(error => {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error?.data?.message : 'Something went wrong.');
      setOpenCloneRoomModal(false);
    });
  };
  const selectHelpOption = (value: string) => {
    setSelectedOption(value);
    if (value === HELPOPTIONSDROPDOWN.WRITEUPS) {
      setOpenWriteupsModal(true);
    } else if (value === HELPOPTIONSDROPDOWN.VIDEO_SOLUTION) {
      setShowVideo(true);
      setOpenWriteupsModal(false);
    }
    setSelectedOption('');
  };
  const onHelpMenuOpenChange = (menuOpen: boolean) => {
    if (menuOpen) {
      // eslint-disable-next-line no-void
      void trackEvent({
        event: 'open-help-menu',
        properties: {
          room_code: roomCode
        }
      });
    }
  };
  const showHelpOptions = useMemo(() => ({
    isVideoSolutionOptionVisible: roomDetails?.video?.free ? roomDetails?.video?.active && roomDetails?.simpleRoom : roomDetails?.video?.active && userData?.data?.user.isPremium && roomDetails?.simpleRoom,
    isWriteupsOptionVisible: !!roomDetails?.writeups?.length && roomDetails?.simpleRoom
  }), [roomDetails?.video?.free, roomDetails?.video?.active, roomDetails?.writeups?.length, userData?.data?.user.isPremium, roomDetails?.simpleRoom]);
  const handleModalOpen = (open: boolean) => {
    setIsShareModalOpen(open);
    if (open) {
      navigate('?reveal=share', {
        replace: true
      });
    } else {
      navigate('', {
        replace: true
      });
    }
  };
  const handleShareAoCModal = (modalOpen: boolean) => {
    setIsShareAoCModalOpen(modalOpen);
    // eslint-disable-next-line no-void
    void trackEvent({
      event: 'click-AoC-CTA',
      properties: {
        room_code: roomCode,
        type: 'room',
        name: roomCode,
        medium: 'AoC-CTA'
      }
    });
    navigate(modalOpen ? '?reveal=share' : '', {
      replace: true
    });
  };
  const isPremium = userData?.data?.user?.isPremium;
  const renderActionsForRoomWithTaskBreakdownSupport = () => <StyledFlex gap={isTablet ? '1.6rem' : '.6rem'} justifyContent="flex-start" data-sentry-element="StyledFlex" data-sentry-component="renderActionsForRoomWithTaskBreakdownSupport" data-sentry-source-file="room-banner-actions.tsx">
      {showMachineButton && <StyledWrapperAttackBoxButton id="start-attackbox">
          <ButtonGroup isDarkVersion>
            <StyledChildButton color="secondary" variant="outlined" type="button" key="three" onClick={handleStartMachine} isLoading={isVmStartLoading} loadingMsg="Starting" data-testid="start-attack-box-btn" disabled={isNetworkV2NotReady}>
              <FontAwesomeIcon icon={faLaptop} /> {isAttackBoxRunning ? 'Show Split View' : `Start ${vmStartType}`}
            </StyledChildButton>
            {!isNetworkV2NotReady && <ButtonDropdown isDarkVersion options={BROWSER_MACHINE_OPTIONS} onOptionSelect={(value: string) => {
          dispatch(setVmStartType(value as VmTypes));
        }} selectedOption={vmStartType} alignContent="end" />}
            <DesktopOnlyAccessPopup open={isOpenDesktopOnlyAccessPopUp} onOpenChange={setIsOpenDesktopOnlyAccessPopUp} closeAction={() => {
          // eslint-disable-next-line no-void
          void startVmHandler(vmStartType);
        }} splitScreenType={TaskType.VM} />
          </ButtonGroup>
        </StyledWrapperAttackBoxButton>}
      {!vmPanelOpen && splitScreenTabs.length > 0 && <Button onClick={() => {
      store.dispatch(setVmPanelOpen(true));
    }} color="add">
          {' '}
          Show Split View
        </Button>}
      {showButton && <StyledButton variant="outlined" color="secondary"
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onClick={() => handleSavedRoom()}>
          <FontAwesomeIcon icon={roomDetails?.saved ? faSolidBookMark : faBookmark} />
          {!isSplitScreenOpen && isTablet && 'Save Room'}
        </StyledButton>}
      <ButtonDropdown isDarkVersion options={getHelpOptions(showHelpOptions)} onOptionSelect={(value: string) => selectHelpOption(value)} onOpenChange={onHelpMenuOpenChange} selectedOption={selectedOption} alignContent="start" buttonLabel={!isSplitScreenOpen && isTablet ? 'Help' : ''} icon={<FontAwesomeIcon icon={faQuestion} />} data-sentry-element="ButtonDropdown" data-sentry-source-file="room-banner-actions.tsx" />
      {!isRetiredRoom && isUserAuth && hasUserJoinedRoom && <ButtonDropdown isDarkVersion options={FILTERED_OPTIONS} selectedOption={selectedOption} alignContent="start" buttonLabel={!isSplitScreenOpen && isTablet ? 'Options' : ''} onOptionSelect={(value: string) => selectOption(value)} icon={<FontAwesomeIcon icon={faGear} />} />}
      <LeaveRoomModal open={openLeaveRoomModal} onOpenChange={setOpenLeaveRoomModal} roomCode={roomCode} asTeam={leaveAsTeam} isTeamsRoom={isTeamsRoom} areChangesInTeamLocked={areChangesInTeamLocked} onClosed={() => {
      setLeaveAsTeam(false);
    }} data-sentry-element="LeaveRoomModal" data-sentry-source-file="room-banner-actions.tsx" />
      <ResetProgressModal open={openResetProgressModal} onOpenChange={setOpenResetProgressModal} roomCode={roomCode} setRoomProgress={setRoomProgress} resetToFirstTask={resetToFirstTask} data-sentry-element="ResetProgressModal" data-sentry-source-file="room-banner-actions.tsx" />
      {!isPremium && <UIStyledButton data-premium as={Link} style={{
      whiteSpace: 'nowrap'
    }} to="/why-subscribe" variant="solid" color="primary" id="cta-room-header-go-premium">
          Go Premium
        </UIStyledButton>}
    </StyledFlex>;
  if (isTaskBreakdownSupported) {
    return renderActionsForRoomWithTaskBreakdownSupport();
  }
  const azureTenantLabButtonText = useMemo(() => {
    if (loadingRequestTenantEnvButton) {
      return 'Tenant requested';
    }
    return isAzureTenantLabsReady ? 'Tenant ready' : 'Request tenant environment';
  }, [loadingRequestTenantEnvButton, isAzureTenantLabsReady]);
  return <StyledFlex justifyContent="flex-start" gap="1.2rem" data-testid="actions" data-sentry-element="StyledFlex" data-sentry-component="RoomBannerActions" data-sentry-source-file="room-banner-actions.tsx">
      {isLoading ? <>
          <StyledLoading width="21.3rem" data-testid="loading-1" />
          <StyledLoading width="8.7rem" />
          <StyledLoading width="13.4rem" />
          <StyledLoading width="11.4rem" />
        </> : <>
          {roomProgress === 100 && isUserAuth && <CompletionShareRoomModal roomCode={roomDetails?.code ?? ''} roomType={roomDetails?.type ?? 'challenge'} isOpenModal={isShareModalOpen} isShareModalOpen={isShareModalOpen} onOpenChange={handleModalOpen} setIsShareModalOpen={setIsShareModalOpen} />}
          {roomCode === 'adventofcyber2024' && roomProgress !== 100 && <ShareAoCModal roomCode={roomDetails?.code ?? 'adventofcyber2024'} isShareAoCModalOpen={isShareAoCModalOpen} setIsShareAoCModalOpen={setIsShareAoCModalOpen} onOpenChange={handleShareAoCModal} />}
          {!vmPanelOpen && splitScreenTabs.length > 0 && <Button onClick={() => {
        store.dispatch(setVmPanelOpen(true));
      }} color="add">
              {' '}
              Show Split View
            </Button>}
          {roomDetails?.certificate && isUserAuth && <Certificate roomCode={roomCode} roomProgress={roomProgress} />}
          {showMachineButton && <StyledWrapperAttackBoxButton id="start-attackbox">
              <ButtonGroup isDarkVersion>
                <StyledChildButton color="secondary" variant="outlined" type="button" key="three" onClick={handleStartMachine} isLoading={isVmStartLoading} loadingMsg="Starting" data-testid="start-attack-box-btn" disabled={isNetworkV2NotReady}>
                  <FontAwesomeIcon icon={faLaptop} /> {isAttackBoxRunning ? `Show Split View` : `Start ${vmStartType}`}
                </StyledChildButton>
                {!isNetworkV2NotReady && <ButtonDropdown isDarkVersion options={BROWSER_MACHINE_OPTIONS} onOptionSelect={(value: string) => {
            dispatch(setVmStartType(value as VmTypes));
          }} selectedOption={vmStartType} alignContent="end" />}
                <DesktopOnlyAccessPopup open={isOpenDesktopOnlyAccessPopUp} onOpenChange={setIsOpenDesktopOnlyAccessPopUp} closeAction={() => {
            // eslint-disable-next-line no-void
            void startVmHandler(vmStartType);
          }} splitScreenType={TaskType.VM} />
              </ButtonGroup>
            </StyledWrapperAttackBoxButton>}
          {Boolean(awardedBadge) && <AwardsBadgePopUp awardedBadge={awardedBadge} />}
          {isUserAuth && isAwsCloudRoom &&
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      <StyledButton variant="outlined" color="secondary" onClick={() => handleCloudDetails()}>
              <FontAwesomeIcon icon={faClouds} style={{
          width: 'auto'
        }} />
              Cloud Details
            </StyledButton>}
          <ButtonDropdown isDarkVersion options={getHelpOptions(showHelpOptions)} onOptionSelect={(value: string) => selectHelpOption(value)} onOpenChange={onHelpMenuOpenChange} selectedOption={selectedOption} alignContent="start" buttonLabel="Help" />
          {showButton && <StyledButton variant="outlined" color="secondary"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={() => handleSavedRoom()}>
              <FontAwesomeIcon icon={savedRoom ? faSolidBookMark : faBookmark} />
              Save Room
            </StyledButton>}
          {!isPrivateOrRetiredRoom && <VotingButtons
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onChange={newVote => handlerVote(newVote)} votesCount={votesData?.data?.votes} userVote={votesData?.data?.userVote} isAuth={isUserAuth} isDisabled={!isUserAuth || !hasUserJoinedRoom} />}
          {!isRetiredRoom && isUserAuth && hasUserJoinedRoom && <ButtonDropdown isDarkVersion options={FILTERED_OPTIONS} selectedOption={selectedOption} alignContent="start" buttonLabel="Options" onOptionSelect={(value: string) => selectOption(value)} icon={<FontAwesomeIcon icon={faGear} />} />}
          {showRequestTenantEnvButton && <Button variant="solid" color="primary" isDisabled={disableRequestTenantEnvButton}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={handleRequestTenantEnv}>
              <FontAwesomeIcon icon={faClouds} />
              {azureTenantLabButtonText}
            </Button>}
          {openCloneRoomModal && <ModalLayout showTriggerElement={false} open={openCloneRoomModal} openChange={setOpenCloneRoomModal} showWarnBox={false} actionButtonText="Clone room" modalTitleText="Clone room" closeButtonText="Close"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      handleActionButton={handleCloneRoom}>
              <p>Are you sure you want to clone this room?</p>
            </ModalLayout>}
          <ResetProgressModal open={openResetProgressModal} onOpenChange={setOpenResetProgressModal} roomCode={roomCode} setRoomProgress={setRoomProgress} resetToFirstTask={resetToFirstTask} />
          <LeaveRoomModal open={openLeaveRoomModal} onOpenChange={setOpenLeaveRoomModal} roomCode={roomCode} asTeam={leaveAsTeam} isTeamsRoom={isTeamsRoom} areChangesInTeamLocked={areChangesInTeamLocked} onClosed={() => {
        setLeaveAsTeam(false);
      }} />
          <AWSCloudDetailsModal open={isOpenAWSCloudDetailsModal} onOpenChange={setIsOpenAWSCloudDetailsModal} environmentStatus={awsEnvironmentStatus} roomsList={awsEnvironmentRoomsList} awsCredentials={awsCredential} roomDetails={roomDetails} />
          <AWSCloudDetailsPopup open={isOpenAWSCloudDetailsPopup} onOpenChange={setIsOpenAWSCloudDetailsPopup} />
          <ShowWriteups open={openWriteupsModal} onOpenChange={setOpenWriteupsModal} roomCode={roomCode} writeupsArray={writeupsArray} />
        </>}
    </StyledFlex>;
};